import type { ContextMenuAction } from '../../../explorer/models/TreeModel';
import type { StorageActionNode } from '../../../explorer/models/nodeTypes';
import type { StorageMenuContext } from '../../../database-objects/services/storageTreeExtensions';
import type { StorageMenuNode } from '../../../database-objects/services/storageContextMenuService';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { toast } from '../../../../components/ui/toaster';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import { openStorageCreateContainerDialog } from './StorageCreateContainerDialog';
import { confirmStorageDelete, requestStorageUpload } from './storageMutations';

export type S2StorageTreeAction = 'create-container' | 'upload' | 'delete';

const item = (id: S2StorageTreeAction, label: string, readOnly: boolean): ContextMenuAction => ({
  id, label, disabled: readOnly, ...(readOnly ? { disabledReason: STORAGE_READ_ONLY_REASON } : {}),
});

export function s2MenuItems(node: StorageMenuNode, ctx: StorageMenuContext): ContextMenuAction[] {
  if (node.type === 'storage-service-folder' && node.metadata.service === 'blob') return [item('create-container', 'Create container…', ctx.readOnly)];
  if (node.type === 'storage-container') return [item('upload', 'Upload…', ctx.readOnly), item('delete', 'Delete…', ctx.readOnly)];
  return [];
}

export function isS2StorageTreeAction(actionId: string): actionId is S2StorageTreeAction {
  return actionId === 'create-container' || actionId === 'upload' || actionId === 'delete';
}

export async function executeS2StorageTreeAction(
  actionId: S2StorageTreeAction,
  node: StorageMenuNode | StorageActionNode,
  deps: { refresh: (nodeId?: string) => void },
): Promise<void> {
  const { connectionId, resource } = node.metadata;
  // Create container… opens its dialog on a read-only connection too, its Create button unavailable with the reason —
  // as Create table… and Create queue… do (release pass 2026-10-01: the row's toast read as "nothing happened").
  if (actionId === 'create-container' && node.metadata.service === 'blob') {
    openStorageCreateContainerDialog({ connectionId, serviceFolderId: node.type === 'storage-action' ? node.parentId ?? node.id : node.id, refresh: () => deps.refresh(node.type === 'storage-action' ? node.parentId ?? node.id : node.id) });
    return;
  }
  if (getConnectionById(connectionId).connection?.readOnly) {
    toast.error({ title: 'Storage action unavailable', description: STORAGE_READ_ONLY_REASON });
    return;
  }
  if (resource.kind !== 'container') return;
  if (actionId === 'upload') {
    // `feature:storage_upload` fires inside requestStorageUpload on a confirmed start (review E F15).
    await requestStorageUpload({ connectionId, destination: resource, sourceKind: 'files', source: 'tree' });
    return;
  }
  if (actionId === 'delete') {
    await confirmStorageDelete({ connectionId, resources: [resource], source: 'tree', refresh: () => deps.refresh(node.parentId ?? node.id) });
  }
}
